import { type DealQueueSort, type DealQueueTab } from '@carsfinders/contracts';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { dealsQueueQueryKey, fetchDealsQueue } from '../api';

/** How long the search box waits after the last keystroke before it queries. */
export const DEALS_SEARCH_DEBOUNCE_MS = 300;

/** `value`, but only once it has stopped changing for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * The «Угоди» queue for one tab + search, paged by the server's keyset cursor.
 *
 * `keepPreviousData` holds the last page on screen while a new tab or search
 * loads, so the tab strip's counters do not blink to nothing on every click.
 * Live refresh is not this hook's job: the shell's single bid stream
 * invalidates `DEALS_QUEUE_QUERY_KEY` on every bid event.
 */
export function useDealsQueue(
  tab: DealQueueTab,
  q: string,
  sort?: DealQueueSort,
  userId?: string,
  auction?: string,
) {
  const query = useInfiniteQuery({
    queryKey: dealsQueueQueryKey(tab, q, sort, userId, auction),
    queryFn: ({ pageParam }) =>
      fetchDealsQueue({ tab, sort, userId, auction, q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });

  const pages = query.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.rows);
  // Counters come with every page and describe the whole source set; the first
  // page's are the ones that match what the operator is looking at.
  const counts = pages[0]?.counts ?? null;
  // The «Клієнт» filter's options — the whole source set, like the counters.
  const clients = pages[0]?.clients ?? [];
  // The «Аукціон» filter's options — same scope rule.
  const auctions = pages[0]?.auctions ?? [];

  return { ...query, rows, counts, clients, auctions };
}
